import { Big } from 'big.js'
import type * as Flows from './types.no-deps'
import { assumeExhaustion } from '../../lib/typeHelpers.no-deps'
import { strictParseFloat } from '../../lib/number.no-deps'
import { findChoiceLabel, findDropdownChoiceValue } from './choice.no-deps'
import { currentAnswerIsEmpty, findAnswer } from './answer.no-deps'
import { type Dictionary, dictValues } from '../../lib/functional/dictionary.no-deps'
import type { ElementFilter, Expression, Query } from './Expression.no-deps'
import type { UnitData } from '../units/types.no-deps'
import type { LoopPathIds } from './LoopPath.no-deps'
import { sum, product, mean, median, mode, maximum, minimum, range, stddev } from '../../lib/big.no-deps'
import { compact, uniq } from '../../lib/functional/array.no-deps'
import { pipe } from '../../lib/functional/pipe.no-deps'
import { getLatest } from '../../lib/date.no-deps'
import { findCalculationResult } from './summary.no-deps'

type Element = Flows.Element | Flows.NestedElement | Flows.Section

export function evaluateCalculation(
    formula: Expression,
    answers: Flows.Answer[],
    previousCalculations: Flows.SectionSummary['calculations'],
    elementsLookup: Dictionary<Element>,
    subjectUnitData: UnitData,
    loopPathIds: LoopPathIds,
    elementToStepLookup: Map<Flows.ElementId, Flows.Step>
): number {
    const result = recurse(
        formula,
        answers,
        previousCalculations,
        elementsLookup,
        subjectUnitData,
        loopPathIds,
        elementToStepLookup
    )
    return result != null ? parseFloat(result.valueOf()) : null
}

function recurse(
    expression: Expression,
    answers: Flows.Answer[],
    previousCalculations: Flows.SectionSummary['calculations'],
    elementsLookup: Dictionary<Element>,
    subjectUnitData: UnitData,
    loopPathIds: LoopPathIds,
    elementToStepLookup: Map<Flows.ElementId, Flows.Step>
): Big {
    switch (expression.type) {
        case 'constant':
            return Big(expression.constant)

        case 'element': {
            const defaultValue = expression.evaluateNullAs != null ? Big(expression.evaluateNullAs) : null
            return getValueForElement(
                expression.elementId,
                answers,
                previousCalculations,
                elementsLookup,
                defaultValue,
                loopPathIds
            )
        }

        case 'operator':
            const left = recurse(
                expression.left,
                answers,
                previousCalculations,
                elementsLookup,
                subjectUnitData,
                loopPathIds,
                elementToStepLookup
            )
            const right = recurse(
                expression.right,
                answers,
                previousCalculations,
                elementsLookup,
                subjectUnitData,
                loopPathIds,
                elementToStepLookup
            )
            if (left == null || right == null) return null

            switch (expression.operator) {
                case '+':
                    return left.plus(right)
                case '-':
                    return left.minus(right)
                case '*':
                    return left.times(right)
                case '/':
                    // Avoid divide by 0 error
                    if (right.eq(0)) return null

                    return left.div(right)
                default:
                    assumeExhaustion(expression.operator)
                    return null
            }

        case 'unit-data-variable':
            const value = subjectUnitData[expression.variableName]
            return typeof value === 'number' ? Big(value) : parseBig(value)

        case 'aggregate': {
            const defaultValue = expression.evaluateNullAs != null ? Big(expression.evaluateNullAs) : null
            const values = evaluateQuery(
                expression.query,
                answers,
                previousCalculations,
                elementsLookup,
                defaultValue,
                loopPathIds,
                elementToStepLookup
            )

            // If there are some null values, then bail with default value
            if (values.some((v) => v == null)) return defaultValue

            // For count, can calcluate if there are no values
            if (expression.aggregateFunction === 'count') return Big(values.length)

            // If there are no values, then bail with default value
            if (values.length === 0) return defaultValue

            switch (expression.aggregateFunction) {
                case 'sum':
                    return sum(values)
                case 'product':
                    return product(values)
                case 'mean':
                    return mean(values)
                case 'median':
                    return median(values)
                case 'mode':
                    return mode(values)[0]
                case 'maximum':
                    return maximum(values)
                case 'minimum':
                    return minimum(values)
                case 'range':
                    return range(values)
                case 'stddev':
                    return stddev(values)
                default:
                    assumeExhaustion(expression.aggregateFunction)
                    return defaultValue
            }
        }

        default:
            assumeExhaustion(expression)
    }
}

function parseBig(str: string): Big | null {
    if (str == null) return null
    if (strictParseFloat(str) == null) return null
    return Big(str)
}

function getValueForElement(
    elementId: Flows.ElementId,
    answers: Flows.Answer[],
    previousCalculations: Flows.SectionSummary['calculations'],
    elementsLookup: Dictionary<Element>,
    defaultValue: Big | null,
    loopPathIds: LoopPathIds
) {
    const previousCalculation = findCalculationResult({ calculations: previousCalculations }, elementId, loopPathIds)
    if (previousCalculation != null) {
        return previousCalculation.value == null ? defaultValue : Big(previousCalculation.value)
    }

    const answer = findAnswer(answers, elementId, loopPathIds)
    if (currentAnswerIsEmpty(answer)) return defaultValue
    if (!isCalculableAnswer(answer)) return defaultValue
    return getValueForElementWithAnswer(elementsLookup[elementId], answer, defaultValue)
}

type CalculableAnswer =
    | { type: 'number'; value: number }
    | { type: 'yes-no'; value: 'yes' | 'no' }
    | { type: 'radio'; value: Flows.ChoiceId }
    | { type: 'dropdown'; value: Flows.ChoiceId[] }
    | { type: 'image-select'; value: Flows.ChoiceId[] }

function isCalculableAnswer(answer: Pick<Flows.Answer, 'type' | 'value'>): answer is CalculableAnswer {
    return (
        answer.type === 'number' ||
        answer.type === 'yes-no' ||
        answer.type === 'radio' ||
        answer.type === 'dropdown' ||
        answer.type === 'image-select'
    )
}

function isCalculableElement(element: Element): boolean {
    return (
        element.type === 'number' ||
        element.type === 'yes-no' ||
        element.type === 'radio' ||
        element.type === 'dropdown' ||
        element.type === 'image-select' ||
        element.type === 'calculation'
    )
}

function getValueForElementWithAnswer(element: Element, answer: CalculableAnswer, defaultValue: Big | null) {
    if (answer.type === 'number') return Big(answer.value)

    if (answer.type === 'yes-no' && element.type === 'yes-no') {
        return Big(answer.value === 'yes' ? (element.yesWeight ?? 1) : (element.noWeight ?? 0))
    }

    if (answer.type === 'radio' && element.type === 'radio') {
        const choiceLabel = findChoiceLabel(element.choices, answer.value)
        return parseBig(choiceLabel) ?? defaultValue
    }

    if (
        (answer.type === 'dropdown' && element.type === 'dropdown') ||
        (answer.type === 'image-select' && element.type === 'image-select')
    ) {
        if (element.maxChoices && element.maxChoices !== 1) return null
        const dropdownChoiceValue = findDropdownChoiceValue(element.choices, answer.value[0])
        const choiceLabel = findChoiceLabel(element.choices, answer.value[0])

        return parseBig(dropdownChoiceValue) ?? parseBig(choiceLabel) ?? defaultValue
    }

    return null
}

/**
 * A query in this context being something that returns an array of values for an aggregate to fold over
 */
function evaluateQuery(
    query: Query,
    answers: Flows.Answer[],
    previousCalculations: Flows.SectionSummary['calculations'],
    elementsLookup: Dictionary<Element>,
    defaultValue: Big | null,
    loopPathIds: LoopPathIds,
    elementToStepLookup: Map<Flows.ElementId, Flows.Step>
): Array<Big | null> {
    switch (query.type) {
        case 'elements-query':
            // An elements query is derived from all the elements in the flow
            let elements = dictValues(elementsLookup)

            // Can filter the elements that are of interest here
            elements = filterElements(query.where, elements, answers, loopPathIds, elementToStepLookup)

            // Select the numerical value of interest from the elements
            switch (query.select) {
                case 'value':
                    elements = elements.filter(isCalculableElement)
                    return elements.map((e) =>
                        getValueForElement(
                            e.id,
                            answers,
                            previousCalculations,
                            elementsLookup,
                            defaultValue,
                            loopPathIds
                        )
                    )
                case 'max-possible-value':
                    elements = elements.filter(isCalculableElement)
                    return elements.map((e) => getMax(getPossibleValuesForElement(e, defaultValue)) ?? defaultValue)
                case 'min-possible-value':
                    elements = elements.filter(isCalculableElement)
                    return elements.map((e) => getMin(getPossibleValuesForElement(e, defaultValue)) ?? defaultValue)
                default:
                    assumeExhaustion(query.select)
                    return []
            }
        default:
            assumeExhaustion(query.type)
            return []
    }
}

function filterElements(
    filter: ElementFilter | undefined,
    elements: Element[],
    answers: Flows.Answer[],
    loopPathIds: LoopPathIds,
    elementToStepLookup: Map<Flows.ElementId, Flows.Step>
): Element[] {
    if (filter == null) return elements

    switch (filter.type) {
        case 'element-filter-and': {
            const left = filterElements(filter.left, elements, answers, loopPathIds, elementToStepLookup)
            const right = filterElements(filter.right, elements, answers, loopPathIds, elementToStepLookup)
            return left.filter((e) => right.includes(e))
        }
        case 'element-filter-or': {
            const left = filterElements(filter.left, elements, answers, loopPathIds, elementToStepLookup)
            const right = filterElements(filter.right, elements, answers, loopPathIds, elementToStepLookup)
            return uniq([...left, ...right])
        }
        case 'element-filter-not':
            const blacklist = filterElements(filter.operand, elements, answers, loopPathIds, elementToStepLookup)
            return elements.filter((e) => !blacklist.includes(e))
        case 'element-in-list':
            return elements.filter((e) => filter.list.includes(e.id))
        case 'element-in-steps':
            return elements.filter((e) => filter.list.includes(elementToStepLookup.get(e.id)?.id))
        case 'element-has-type':
            return elements.filter((e) => filter.elementType === e.type)
        case 'element-has-answer':
            return elements.filter((e) => !currentAnswerIsEmpty(findAnswer(answers, e.id, loopPathIds)))
        default:
            assumeExhaustion(filter)
            return elements
    }
}

function getPossibleValuesForElement(element: Element, defaultValue: Big | null) {
    if (element.type === 'yes-no') {
        return [
            getValueForElementWithAnswer(element, { type: 'yes-no', value: 'yes' }, defaultValue),
            getValueForElementWithAnswer(element, { type: 'yes-no', value: 'no' }, defaultValue),
        ]
    }

    if (element.type === 'radio') {
        return element.choices.map((c) =>
            getValueForElementWithAnswer(element, { type: element.type, value: c.id }, defaultValue)
        )
    }

    if (element.type === 'dropdown') {
        if (element.maxChoices != null && element.maxChoices !== 1) return []

        return element.choices.map((c) =>
            getValueForElementWithAnswer(element, { type: element.type, value: [c.id] }, defaultValue)
        )
    }

    if (element.type === 'image-select') {
        if (element.maxChoices != null && element.maxChoices !== 1) return []

        return element.choices.map((c) =>
            getValueForElementWithAnswer(element, { type: element.type, value: [c.id] }, defaultValue)
        )
    }

    return []
}

function getMax(values: Big[]) {
    values = compact(values)
    return values.reduce((max, value) => (max.lt(value) ? value : max), values[0])
}

function getMin(values: Big[]) {
    values = compact(values)
    return values.reduce((max, value) => (max.gt(value) ? value : max), values[0])
}

export function calcutionTimestamp(
    expression: Expression,
    answers: Flows.Answer[],
    previousCalculations: Flows.SectionSummary['calculations'],
    loopPathIds: LoopPathIds,
    elementsLookup: Dictionary<Element>,
    elementToStepLookup: Map<Flows.ElementId, Flows.Step>
): Date {
    switch (expression.type) {
        case 'constant':
            return null

        case 'element':
            const calculation = findCalculationResult(
                { calculations: previousCalculations },
                expression.elementId,
                loopPathIds
            )
            if (calculation != null) return calculation.timestamp

            const answer = findAnswer(answers, expression.elementId, loopPathIds)
            return answer != null ? answer.timestamp : null

        case 'operator':
            const left = calcutionTimestamp(
                expression.left,
                answers,
                previousCalculations,
                loopPathIds,
                elementsLookup,
                elementToStepLookup
            )
            const right = calcutionTimestamp(
                expression.right,
                answers,
                previousCalculations,
                loopPathIds,
                elementsLookup,
                elementToStepLookup
            )
            if (left == null) return right
            if (right == null) return left
            return left > right ? left : right

        case 'unit-data-variable':
            return null

        case 'aggregate':
            return getQueryTimestamp(
                expression.query,
                answers,
                previousCalculations,
                loopPathIds,
                elementsLookup,
                elementToStepLookup
            )

        default:
            assumeExhaustion(expression)
    }
}

function getQueryTimestamp(
    query: Query,
    answers: Flows.Answer[],
    previousCalculations: Flows.SectionSummary['calculations'],
    loopPathIds: LoopPathIds,
    elementsLookup: Dictionary<Element>,
    elementToStepLookup: Map<Flows.ElementId, Flows.Step>
): Date {
    switch (query.type) {
        case 'elements-query':
            // An elements query is derived from all the elements in the flow
            let elements = dictValues(elementsLookup)

            // Can filter the elements that are of interest here
            elements = filterElements(query.where, elements, answers, loopPathIds, elementToStepLookup)

            // If the query is selecting the value, then we filter out things that don't have a numerical value
            if (
                query.select === 'value' ||
                query.select === 'max-possible-value' ||
                query.select === 'min-possible-value'
            ) {
                elements = elements.filter(isCalculableElement)
            }

            // Get the most recent timestamp from an answer or calculation
            const latestTimestamp = pipe(
                elements.map((element) => {
                    const answer = findAnswer(answers, element.id, loopPathIds)

                    const calculation = findCalculationResult(
                        { calculations: previousCalculations },
                        element.id,
                        loopPathIds
                    )

                    return answer?.timestamp ?? calculation?.timestamp
                }),
                compact,
                getLatest
            )

            // Select the numerical value of interest from the elements
            switch (query.select) {
                case 'value':
                    return latestTimestamp ?? null
                case 'max-possible-value':
                    return null
                case 'min-possible-value':
                    return null
                default:
                    assumeExhaustion(query.select)
                    return null
            }
        default:
            assumeExhaustion(query.type)
            return null
    }
}
